import React from "react";
import {
  Autocomplete,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  NavigateNext as BreadcrumbIcon,
  HelpOutline as UnclassifiedIcon,
  SmartToy as OpenAiIcon,
  Edit as ManualIcon,
} from "@mui/icons-material";
import type { Product } from "./types";

/**
 * Componente que renderiza la celda de categoría de un producto en la
 * tabla CategoryReviewPage. Extraído como componente dedicado para
 * mantener la página ≤500L (P7). Sin cambio funcional.
 */

interface CategoryCellProps {
  product: Product;
  isEditing: boolean;
  editValue: string;
  setEditValue: (v: string) => void;
  existingCategories: string[];
  onStartEdit: (product: Product) => void;
}

export function CategoryCell({
  product: p,
  isEditing,
  editValue,
  setEditValue,
  existingCategories,
  onStartEdit,
}: CategoryCellProps): React.ReactElement {
  if (isEditing) {
    return (
      <Autocomplete
        size="small"
        freeSolo
        autoHighlight
        options={["__RESET__", ...existingCategories]}
        getOptionLabel={(opt) =>
          opt === "__RESET__" ? "Ninguna (reclasificar)" : opt
        }
        renderOption={(props, opt) =>
          opt === "__RESET__" ? (
            <li
              {...props}
              key="__RESET__"
              style={{ color: "#ed6c02", fontStyle: "italic" }}
            >
              Ninguna (reclasificar)
            </li>
          ) : (
            <li {...props} key={opt}>
              {opt}
            </li>
          )
        }
        value={editValue || null}
        onChange={(_e, newVal) =>
          setEditValue(typeof newVal === "string" ? newVal : "")
        }
        onInputChange={(_e, newInput) => setEditValue(newInput)}
        sx={{ minWidth: 220 }}
        renderInput={(params) => {
          const {
            InputLabelProps: _label,
            InputProps,
            inputProps,
            ...rest
          } = params;
          return (
            <TextField
              {...rest}
              slotProps={{
                input: { ...InputProps },
                htmlInput: { ...inputProps },
              }}
              placeholder="Categoria..."
              autoFocus
              size="small"
            />
          );
        }}
      />
    );
  }

  if (!p.unified_category) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={() => onStartEdit(p)}
        sx={{
          cursor: "pointer",
          py: 0.5,
          px: 1,
          borderRadius: 1,
          border: "1px dashed",
          borderColor: "warning.light",
          bgcolor: "rgba(255, 152, 0, 0.04)",
          transition: "all 0.15s",
          "&:hover": {
            bgcolor: "rgba(255, 152, 0, 0.10)",
            borderColor: "warning.main",
          },
        }}
      >
        <UnclassifiedIcon sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography
          variant="body2"
          color="warning.main"
          fontWeight={500}
          sx={{ fontSize: "0.8rem" }}
        >
          Sin clasificar
        </Typography>
      </Stack>
    );
  }

  const parts = p.unified_category.split(" / ");
  const leaf = parts[parts.length - 1];
  const parents = parts.slice(0, -1);
  const sourceIcon =
    p.category_source === "openai" ? (
      <OpenAiIcon
        sx={{ fontSize: 13, color: "primary.main", opacity: 0.7 }}
      />
    ) : p.category_source === "manual" ? (
      <ManualIcon
        sx={{ fontSize: 13, color: "success.main", opacity: 0.7 }}
      />
    ) : null;

  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Ruta completa:
          </Typography>
          {parts.map((part, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ pl: i * 1.5 }}
            >
              {i > 0 && (
                <BreadcrumbIcon sx={{ fontSize: 12, opacity: 0.5 }} />
              )}
              <Typography
                variant="caption"
                sx={{ fontWeight: i === parts.length - 1 ? 700 : 400 }}
              >
                {part}
              </Typography>
            </Stack>
          ))}
          {p.category_source && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, opacity: 0.7, fontStyle: "italic" }}
            >
              Fuente: {p.category_source}
            </Typography>
          )}
        </Stack>
      }
      arrow
      placement="top-start"
    >
      <Stack
        onClick={() => onStartEdit(p)}
        sx={{
          cursor: "pointer",
          py: 0.5,
          px: 1,
          borderRadius: 1,
          border: "1px solid",
          borderColor:
            p.category_source === "manual" ? "success.light" : "divider",
          bgcolor:
            p.category_source === "manual"
              ? "rgba(76, 175, 80, 0.04)"
              : "rgba(0,0,0,0.01)",
          transition: "all 0.15s",
          "&:hover": {
            bgcolor:
              p.category_source === "manual"
                ? "rgba(76, 175, 80, 0.10)"
                : "rgba(0,0,0,0.04)",
            borderColor: "primary.light",
          },
        }}
      >
        {parents.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.3}
            sx={{ flexWrap: "nowrap", overflow: "hidden" }}
          >
            {parents.map((parent, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <BreadcrumbIcon
                    sx={{
                      fontSize: 11,
                      color: "text.disabled",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    lineHeight: 1.2,
                    flexShrink: i === parents.length - 1 ? 1 : 0,
                  }}
                >
                  {parent}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              color: "text.primary",
              fontSize: "0.82rem",
            }}
          >
            {leaf}
          </Typography>
          {sourceIcon}
        </Stack>
      </Stack>
    </Tooltip>
  );
}
